import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Play, Wrench,
  ChevronRight, MessageCircle, Printer, ChevronDown, ChevronUp
} from 'lucide-react';

const diagnosisSteps = [
  {
    id: 1,
    title: 'Check hydraulic pressure',
    detail: 'Verify the pressure gauge shows between 2,500-3,000 PSI during operation.',
    status: 'pass' as const,
    result: 'Pressure gauge shows 2,800 PSI (normal)',
  },
  {
    id: 2,
    title: 'Verify electrical connections',
    detail: 'Inspect all power cable connectors for tightness and corrosion.',
    status: 'issue' as const,
    result: 'Main power cable loose at connector',
  },
];

const relatedIssues = [
  'Tongs operate but no torque reading',
  'Hydraulic pump cycling continuously',
  'Display shows error code E-47',
];

const preventionTips = [
  'Daily connector inspection',
  'Weekly torque check on all connections',
  'Monthly cable replacement schedule',
];

export default function TroubleshootingGuide() {
  const navigate = useNavigate();
  const [expandedStep, setExpandedStep] = useState<number | null>(2);

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Troubleshooting</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* Issue Title */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Wrench className="w-5 h-5 text-fieldiq-accent" />
            <h2 className="text-sm font-bold text-white">Power Tongs Won't Start</h2>
          </div>
          <p className="text-[10px] text-fieldiq-muted ml-7">Follow the steps below to diagnose the issue</p>
        </div>

        {/* Diagnosis Steps */}
        <section>
          <h3 className="text-xs font-bold text-white mb-2">Quick Diagnosis</h3>
          <div className="space-y-2">
            {diagnosisSteps.map((step) => (
              <div key={step.id} className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center gap-3 px-3 py-3 text-left"
                  onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                >
                  <span className="w-5 h-5 rounded-full bg-fieldiq-dark flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                    {step.id}
                  </span>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-white">{step.title}</p>
                  </div>
                  {step.status === 'pass' ? (
                    <span className="text-[8px] border border-green-500/50 text-green-400 px-1.5 py-0.5 rounded-full">PASS</span>
                  ) : (
                    <span className="text-[8px] border border-amber-500/50 text-amber-400 px-1.5 py-0.5 rounded-full">ISSUE</span>
                  )}
                  {expandedStep === step.id ? <ChevronUp className="w-3 h-3 text-fieldiq-muted" /> : <ChevronDown className="w-3 h-3 text-fieldiq-muted" />}
                </button>
                {expandedStep === step.id && (
                  <div className="px-3 pb-3 border-t border-fieldiq-border pt-2">
                    <p className="text-[10px] text-fieldiq-muted mb-2">{step.detail}</p>
                    <div className={`text-xs p-2 rounded-lg ${step.status === 'pass' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {step.result}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Solution */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h3 className="text-sm font-bold text-green-400">Solution Found</h3>
          </div>
          <p className="text-xs text-white mb-3">
            Tighten main power connector. Torque to 35 ft-lbs using calibrated torque wrench.
          </p>
          <div className="flex gap-2">
            <button className="flex-1 flex items-center justify-center gap-1 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold py-2 rounded-lg hover:bg-amber-400 transition-colors">
              <Play className="w-3.5 h-3.5" /> Watch Fix (2:30)
            </button>
            <button className="flex items-center justify-center gap-1 bg-fieldiq-card border border-fieldiq-border text-white text-xs font-medium py-2 px-3 rounded-lg hover:border-fieldiq-accent transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>

        {/* Prevention */}
        <section>
          <h3 className="text-xs font-bold text-white mb-2">Prevention</h3>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 space-y-2">
            {preventionTips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-fieldiq-accent mt-0.5 shrink-0" />
                <p className="text-xs text-fieldiq-text">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related Issues */}
        <section>
          <h3 className="text-xs font-bold text-white mb-2">Related Issues</h3>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {relatedIssues.map((issue) => (
              <button key={issue} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-fieldiq-dark/50 transition-colors">
                <Wrench className="w-4 h-4 text-fieldiq-muted shrink-0" />
                <p className="text-xs text-white flex-1">{issue}</p>
                <ChevronRight className="w-4 h-4 text-fieldiq-muted" />
              </button>
            ))}
          </div>
        </section>

        {/* Help */}
        <button className="w-full flex items-center justify-center gap-1 bg-fieldiq-card border border-fieldiq-border text-white text-xs font-medium py-2.5 rounded-xl hover:border-fieldiq-accent transition-colors">
          <MessageCircle className="w-4 h-4" /> Still Need Help? Chat with Expert
        </button>
      </div>
    </div>
  );
}
